import type { PageEntry, Entry, Node } from 'contensis-delivery-api';
import dashify from 'dashify';
import { Tick } from '@solent-university/solent-icons';

import type { EventsVariants } from 'Theme/types/components.type';
import cmsParser from 'Common/util/cmsParser';
import { composerHeroBanner } from 'Common/util/cmsComposer';

import type { Props } from './Landing';

export type PageData = {
    entry: PageEntry;
    upcomingOpenDays: Entry[] | undefined;
    ancestors: Node[];
};

const datamap = (data: PageData): Props => {
    const { entry, upcomingOpenDays, ancestors } = data;

    const {
        title,
        sys,

        // Hero Banner
        heroBanner,
        upcomingEventsHeading,

        // Intro
        descriptionText,
        tickboxList,

        // Open day events
        openDaysUpcomingEvents,

        // Voucher
        voucherValue,
        promoPointHeading,
        promoPointDescription,
        promoPointLink,

        // Campus Tour,
        otherEventsHeading,
        otherEventsDescription,
        otherEventsLink,

        // Contact Us
        contactHeading,
        secondContactEmail,
        socialLinks
    } = entry;

    const NUMBER_OF_EVENTS_TO_SHOW = 3;

    return {
        breadcrumbs: cmsParser.breadcrumbs([...ancestors, { entry: { title: title }, id: sys.id }]),
        heroBanner: composerHeroBanner.ImageVideo(heroBanner[0]),
        upcomingOpenDays: {
            heading: upcomingEventsHeading,
            events: upcomingOpenDays
                ? upcomingOpenDays.slice(0, NUMBER_OF_EVENTS_TO_SHOW).map(event => {
                      return {
                          id: event.sys.id,
                          type: event.eventType,
                          variant: event.sys.contentTypeId as EventsVariants,
                          date: new Date(event.date.from),
                          delivery: event.delivery,
                          link: {
                              label: 'Book now',
                              ariaLabel: `Click to book ${event.eventType} open day now`,
                              href: event.bookingLink.entryTitle,
                              openInNewTab: true
                          }
                      };
                  })
                : []
        },
        events: {
            intro: descriptionText,
            list: {
                heading: tickboxList.label,
                items: tickboxList.item.map(item => ({ label: item, icon: Tick }))
            }
        },
        eventBoxes: openDaysUpcomingEvents.map(upcomingEventType => {
            const eventTypeURL = upcomingEventType.eventGroupParentPage.sys.uri;
            return {
                id: upcomingEventType.heading.replace(/ /g, '-').toLowerCase(),
                heading: upcomingEventType.heading,
                icon: Tick,
                subline: upcomingEventType.rightForMeHeading,
                text: upcomingEventType.rightForMeDescription,
                events: upcomingOpenDays
                    ? upcomingOpenDays
                          .filter(event => event.eventType === upcomingEventType.eventType)
                          .slice(0, NUMBER_OF_EVENTS_TO_SHOW)
                          .map(event => ({
                              id: event.sys.id,
                              type: dashify(event.sys.contentTypeId),
                              date: new Date(event.date.from),
                              location: event.delivery,
                              link: {
                                  label: 'Find out more',
                                  ariaLabel: 'Click to read about this event',
                                  href: `${eventTypeURL}#${event.sys.id}`,
                                  iconOutline: true
                              }
                          }))
                    : [],
                variant: upcomingEventType.eventGroupParentPage.childContentType as EventsVariants
            };
        }),
        voucher: {
            heading: voucherValue,
            subheading: promoPointHeading,
            information: promoPointDescription,
            link: { ...cmsParser.link(promoPointLink), variant: 'sixDark' }
        },
        campusTour: {
            id: 'campus-tour',
            heading: otherEventsHeading,
            description: otherEventsDescription,
            link: cmsParser.linkButton(otherEventsLink)
        },
        contactUs: {
            id: 'contact-us',
            heading: contactHeading,
            generalQuestions: {
                link: {
                    label: secondContactEmail.email,
                    ariaLabel: 'Click to contact Solent',
                    href: `mailto:${secondContactEmail.email}`,
                    openInNewTab: true
                }
            },
            socials: Object.fromEntries(socialLinks.map(item => [item.socialMediaType, item.link])),
            useWave: false
        }
    };
};

export default datamap;
